import { useEffect, useState } from "react";

import { LAYOUT_HEADER_HEIGHT } from "@ryde/shared";

import { useLayoutMainContext } from "../../contexts";

const resolveMainPaddingBottom = (mainElement: HTMLElement | null): number => {
  if (!mainElement) {
    return 0;
  }

  return (
    Number.parseFloat(window.getComputedStyle(mainElement).paddingBottom) || 0
  );
};

const resolveViewportHeight = ({
  headerElement,
  mainElement,
}: {
  headerElement: HTMLElement | null;
  mainElement: HTMLElement | null;
}): number => {
  const mainPaddingBottom = resolveMainPaddingBottom(mainElement);

  if (headerElement) {
    const headerBottom = headerElement.getBoundingClientRect().bottom;

    return Math.max(0, window.innerHeight - headerBottom - mainPaddingBottom);
  }

  const fallbackTop = mainElement?.getBoundingClientRect().top ?? 0;

  return Math.max(
    0,
    window.innerHeight - fallbackTop - LAYOUT_HEADER_HEIGHT - mainPaddingBottom,
  );
};

export const useLayoutViewportHeight = (): number => {
  const { headerElement, mainElement } = useLayoutMainContext();

  const [viewportHeight, setViewportHeight] = useState(() =>
    resolveViewportHeight({ headerElement, mainElement }),
  );

  const syncViewportHeight = (): void => {
    const nextHeight = resolveViewportHeight({ headerElement, mainElement });
    setViewportHeight(previousHeight =>
      previousHeight === nextHeight ? previousHeight : nextHeight,
    );
  };

  useEffect(() => {
    syncViewportHeight();
    // headerElement/mainElement identity is tracked via dedicated effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerElement, mainElement]);

  useEffect(() => {
    const onWindowResize = (): void => {
      syncViewportHeight();
    };

    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerElement, mainElement]);

  useEffect(() => {
    if (!headerElement) {
      return undefined;
    }

    syncViewportHeight();

    const resizeObserver = new ResizeObserver(syncViewportHeight);
    resizeObserver.observe(headerElement);

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerElement, mainElement]);

  return viewportHeight;
};
